import { ErrorMessage, useFormikContext } from "formik"
import { Col } from "react-bootstrap"
import { useTranslation } from "react-i18next"

import DirectionText from "@/components/DirectionText/DirectionText"
import TextboxInput from "@/components/Inputs/TextboxInput"
import type { SurveyQuestion, SurveySubmitData } from "@/types/surveyTypes"

export default function Question({
  question,
  isAnswered,
}: {
  question: SurveyQuestion
  isAnswered: boolean
}) {
  const { values, setFieldValue } = useFormikContext<SurveySubmitData>()
  const { t } = useTranslation()

  const handleSelect = (option: string) => {
    if (isAnswered) return

    const existingIndex = values.answers.findIndex(
      (ans) => ans.survey_question_id === question.id
    )

    let newAnswers
    if (existingIndex !== -1) {
      newAnswers = values?.answers.map((ans, i) =>
        i === existingIndex ? { ...ans, answer: option } : ans
      )
    } else {
      newAnswers = [
        ...values.answers,
        { survey_question_id: question.id, answer: option },
      ]
    }

    setFieldValue("answers", newAnswers)
  }

  const selectedAnswer = values.answers.find(
    (ans) => ans.survey_question_id === question.id
  )

  // this is the special notes question that is added to every survey
  const isNotesQuestion =
    question.question_text === "Notes" && question.question_type === "textarea"

  return (
    <Col md={12} className="mb-5">
      {(() => {
        if (question.question_type === "radio") {
          if (question.question_text === "Recommend") {
            return (
              <DirectionText as="div" className="fw-bold mb-2">
                {t("surveyAnswerPage.recommend.message")}
              </DirectionText>
            )
          } else {
            return (
              <DirectionText as="div" className="fw-bold mb-2">
                {question.question_text}
              </DirectionText>
            )
          }
        } else {
          return null
        }
      })()}

      {/* {!isNotesQuestion &&
                <DirectionText as="div" className="fw-bold mb-2">
          {question.question_text}
        </DirectionText>`} */}

      {(() => {
        if (question.question_type === "radio") {
          // this is the special recommend question that is added to every survey
          if (question.question_text === "Recommend") {
            return question.options?.map((opt, index, options) => {
              const isActive = selectedAnswer?.answer === opt
              const optText =
                index === 0
                  ? `${opt} ${t("surveyAnswerPage.recommend.low")}`
                  : index === options.length - 1
                    ? `${opt} ${t("surveyAnswerPage.recommend.high")}`
                    : opt
              return (
                <RadioAnswer
                  key={`${question.id}-${opt}-${index}`}
                  optText={optText}
                  opt={opt}
                  isActive={isActive}
                  handleSelect={handleSelect}
                  question={question}
                  index={index}
                />
              )
            })
          } else {
            return question.options?.map((opt, index) => {
              const isActive = selectedAnswer?.answer === opt
              return (
                <RadioAnswer
                  key={`${question.id}-${opt}-${index}`}
                  optText={opt}
                  opt={opt}
                  isActive={isActive}
                  handleSelect={handleSelect}
                  question={question}
                  index={index}
                />
              )
            })
          }
        } else if (isNotesQuestion) {
          return (
            <TextboxInput
              label={t("surveyAnswerPage.note")}
              placeholder={t("surveyAnswerPage.note")}
              name="note"
              id="note"
              as="field"
              error={<ErrorMessage name="note" />}
            />
          )
        } else {
          return null
        }
      })()}
    </Col>
  )
}

function RadioAnswer({
  opt,
  optText,
  isActive,
  handleSelect,
  question,
  index,
}: {
  opt: string
  optText: string
  isActive: boolean
  handleSelect: (option: string) => void
  question: SurveyQuestion
  index: number
}) {
  return (
    <Col
      md={12}
      className={`option d-flex align-items-center gap-3 p-2 mb-2 rounded ${
        isActive ? "active" : ""
      }`}
      style={{
        border: `1px solid ${isActive ? "#2370D6" : "#ddd"} `,
        cursor: "pointer",
      }}
      onClick={() => handleSelect(opt)}
    >
      <DirectionText as="div" className="flex-grow-1">
        {optText}
      </DirectionText>

      <div className="custom-radio d-flex justify-content-center align-items-center">
        <input
          type="radio"
          id={`q${question.id}-${index}`}
          name={`q${question.id}`}
          checked={isActive}
          onChange={() => handleSelect(opt)}
        />
        <label htmlFor={`q${question.id}-${index}`}></label>
      </div>
    </Col>
  )
}
